import { Link, useLocation } from 'react-router-dom';
import {
  Sun,
  ListTodo,
  FolderKanban,
  FileText,
  StickyNote,
  Mail,
  CalendarDays,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  MessageSquare,
  Search,
  Presentation,
  Receipt,
  Workflow,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { useAuth } from '@/features/auth/useAuth';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'My Day', href: '/my-day', icon: Sun },
  { label: 'Email', href: '/email', icon: Mail },
  { label: 'Calendar', href: '/calendar', icon: CalendarDays },
  { label: 'Tasks', href: '/tasks', icon: ListTodo },
  { label: 'Projects', href: '/projects', icon: FolderKanban },
  { label: 'Documents', href: '/documents', icon: FileText },
  { label: 'Contacts', href: '/contacts', icon: Users },
  { label: 'Chat', href: '/chat', icon: MessageSquare },
  { label: 'Search', href: '/search', icon: Search },
  { label: 'Presentations', href: '/presentations', icon: Presentation },
  { label: 'Finance', href: '/finance', icon: Receipt },
  { label: 'Workflows', href: '/workflows', icon: Workflow },
  { label: 'Time', href: '/time-tracking', icon: Clock },
  { label: 'Notes', href: '/notes', icon: StickyNote },
];

const BOTTOM_ITEMS: NavItem[] = [
  { label: 'Settings', href: '/settings', icon: Settings, adminOnly: true },
];

export function Sidebar() {
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const profile = useAuthStore((s) => s.profile);
  const { signOut } = useAuth();

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 hidden flex-col border-r bg-card transition-all duration-200 lg:flex',
        sidebarCollapsed ? 'w-16' : 'w-60',
      )}
    >
      {/* Logo / Toggle */}
      <div className="flex h-14 items-center justify-between border-b bg-primary px-3">
        {!sidebarCollapsed && (
          <span className="text-lg font-bold tracking-wide text-primary-foreground">KAYCOO</span>
        )}
        <button
          onClick={toggleSidebar}
          className="rounded-md p-1.5 text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground"
        >
          {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Main Nav */}
      <nav className="flex-1 space-y-1 px-2 py-3">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                sidebarCollapsed && 'justify-center px-2',
              )}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom Nav */}
      <div className="border-t px-2 py-3 space-y-1">
        {BOTTOM_ITEMS.map((item) => {
          if (item.adminOnly && profile?.role !== 'admin') return null;
          const isActive = location.pathname.startsWith(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                sidebarCollapsed && 'justify-center px-2',
              )}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!sidebarCollapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
        <button
          onClick={() => signOut()}
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive',
            sidebarCollapsed && 'justify-center px-2',
          )}
          title={sidebarCollapsed ? 'Sign out' : undefined}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!sidebarCollapsed && <span>Sign out</span>}
        </button>
      </div>
    </aside>
  );
}
